/**
 * Void confirm (README "Void requires a reason"), plus the small dialog shell
 * the detail page's modals share: Escape and a click outside close it, Tab
 * stays inside, and focus moves into it on open and back out on close.
 */
import React, { useEffect, useRef, useState } from 'react';
import InvoiceRepository, { InvoiceDoc } from '../../../internals/repository/InvoiceRepository';
import { errorText } from '../detailModel';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Dialog({
  title, sub, width = 520, onClose, children, footer, busy,
}: {
  title: React.ReactNode;
  sub?: React.ReactNode;
  width?: number;
  onClose: () => void;
  children?: React.ReactNode;
  footer: React.ReactNode;
  /** While a request runs the dialog cannot be dismissed. */
  busy?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useRef(`dlg-${Math.random().toString(36).slice(2, 8)}`).current;

  useEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('input, select, textarea') ?? ref.current?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
    return () => back?.focus?.();
  }, []);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (!busy) onClose();
      return;
    }
    if (e.key !== 'Tab' || !ref.current) return;
    const items = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (!items.length) return;
    const [first, last] = [items[0], items[items.length - 1]];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  return (
    <div className="nl-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKey}
        className="nl-modal flex flex-col"
        style={{ maxWidth: width }}
      >
        <div className="flex flex-col gap-1 px-6 pb-1 pt-[22px]">
          <h2 id={titleId} className="m-0 text-[20px] font-semibold text-text1">{title}</h2>
          {sub && <p className="m-0 text-[13px] text-text2">{sub}</p>}
        </div>
        {children && <div className="px-6 py-4">{children}</div>}
        <div className="flex flex-wrap items-center justify-end gap-2.5 border-t border-borderc px-6 py-3.5">{footer}</div>
      </div>
    </div>
  );
}

export function ErrorBanner({ text, children }: { text?: string | null; children?: React.ReactNode }) {
  if (!text) return null;
  return (
    <div role="alert" className="rounded-lg border border-neg/30 bg-neg-soft px-3.5 py-2.5 text-[13px]" style={{ color: 'var(--negFg)' }}>
      {text}
      {children}
    </div>
  );
}

export default function VoidModal({
  shopId, doc, onClose, onDone,
}: {
  shopId: string;
  doc: InvoiceDoc;
  onClose: () => void;
  onDone: (doc: InvoiceDoc) => void;
}) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const empty = !reason.trim();

  const submit = async () => {
    setTouched(true);
    if (empty || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await InvoiceRepository.void(shopId, doc.docId, reason.trim());
      onDone(res.document);
    } catch (e: any) {
      setError(errorText(e, { invalid_state: 'Only an issued invoice can be voided. Delete a draft instead.' }));
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={`Void ${doc.number ?? 'invoice'}`}
      sub="The invoice stays on record with a VOID mark. The customer link shows a void notice and reminders stop."
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          <button type="button" className="nl-btn nl-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="nl-btn nl-btn--danger" onClick={submit} disabled={busy}>
            {busy ? 'Voiding…' : 'Void invoice'}
          </button>
        </>
      )}
    >
      <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-text2">
          Reason
          <input
            className="nl-input"
            value={reason}
            maxLength={280}
            placeholder="e.g. Event cancelled"
            aria-invalid={touched && empty}
            style={touched && empty ? { borderColor: 'var(--neg)' } : undefined}
            onChange={(e) => { setReason(e.target.value); setError(null); }}
          />
          {touched && empty && <span className="text-[12px] font-medium" style={{ color: 'var(--negFg)' }}>! Enter a reason.</span>}
          <span className="text-right text-[11px] font-normal text-text3">{reason.length}/280</span>
        </label>
        <ErrorBanner text={error} />
      </form>
    </Dialog>
  );
}
